// src/common.ts
import { z } from "zod";
var uuidSchema = z.string().uuid({ message: "Neplatn\xE9 ID" });
var emailSchema = z.string({ required_error: "Email je povinn\xFD" }).email({ message: "Neplatn\xFD email" });
var optionalEmailSchema = z.string().email({ message: "Neplatn\xFD email" }).optional().or(z.literal(""));
var phoneSchema = z.string().regex(/^[\d\s+\-()]{9,}$/, { message: "Neplatn\xE9 telefonn\xED \u010D\xEDslo" }).optional().or(z.literal(""));
var leadTypeEnum = z.enum(
  ["sale", "purchase", "rent", "hypo", "invest", "pm", "other"],
  { required_error: "Typ leadu je povinn\xFD" }
);
var leadStatusEnum = z.enum(
  ["open", "won", "lost", "archived", "spam"]
);
var contactTypeEnum = z.enum(
  ["lead", "client", "partner", "supplier", "referral", "other"]
);
var contactScopeEnum = z.enum(["b2b", "b2c"]);
var temperatureEnum = z.enum(["cold", "warm", "hot"]);
var campaignStatusEnum = z.enum(
  ["draft", "active", "paused", "completed"]
);
var assignStrategyEnum = z.enum(
  ["none", "round_robin", "geo", "tag_based"]
);

// src/auth.ts
import { z as z2 } from "zod";
var loginSchema = z2.object({
  email: emailSchema,
  password: z2.string({ required_error: "Heslo je povinn\xE9" }).min(8, { message: "Heslo mus\xED m\xEDt alespo\u0148 8 znak\u016F" })
});
var registerSchema = z2.object({
  name: z2.string({ required_error: "Jm\xE9no je povinn\xE9" }).min(1, { message: "Jm\xE9no je povinn\xE9" }),
  email: emailSchema,
  password: z2.string({ required_error: "Heslo je povinn\xE9" }).min(8, { message: "Heslo mus\xED m\xEDt alespo\u0148 8 znak\u016F" })
});

// src/lead.ts
import { z as z3 } from "zod";
var createLeadSchema = z3.object({
  project_id: uuidSchema,
  first_name: z3.string({ required_error: "Jm\xE9no je povinn\xE9" }).min(1, { message: "Jm\xE9no je povinn\xE9" }),
  last_name: z3.string().optional(),
  email: optionalEmailSchema,
  phone: phoneSchema,
  lead_type: leadTypeEnum.default("other"),
  expected_value: z3.coerce.number().nonnegative().optional(),
  notes: z3.string().optional(),
  source: z3.string().optional(),
  assigned_to: uuidSchema.optional(),
  pipeline_id: uuidSchema.optional(),
  stage_id: uuidSchema.optional()
});
var updateLeadSchema = z3.object({
  stageId: z3.string().optional(),
  assignedTo: z3.string().optional(),
  status: leadStatusEnum.optional(),
  expectedValue: z3.coerce.number().nonnegative().optional(),
  notes: z3.string().optional(),
  leadType: z3.string().optional()
});

// src/campaign.ts
import { z as z4 } from "zod";
var campaignOutcomeSchema = z4.object({
  name: z4.string({ required_error: "N\xE1zev v\xFDsledku je povinn\xFD" }).min(1, { message: "N\xE1zev v\xFDsledku je povinn\xFD" }),
  code: z4.string({ required_error: "K\xF3d v\xFDsledku je povinn\xFD" }).min(1, { message: "K\xF3d v\xFDsledku je povinn\xFD" }),
  rewardAmount: z4.coerce.number().nonnegative().optional(),
  createsLead: z4.boolean().optional(),
  removesFromQueue: z4.boolean().optional(),
  orderIndex: z4.coerce.number().int().nonnegative().optional()
});
var createCampaignSchema = z4.object({
  name: z4.string({ required_error: "N\xE1zev kampan\u011B je povinn\xFD" }).min(1, { message: "N\xE1zev kampan\u011B je povinn\xFD" }),
  projectId: uuidSchema,
  description: z4.string().optional(),
  batchSize: z4.coerce.number().int().positive().default(25),
  scriptContent: z4.string().optional(),
  outcomes: z4.array(campaignOutcomeSchema).optional(),
  operatorIds: z4.array(uuidSchema).optional()
});
var updateCampaignSchema = z4.object({
  name: z4.string().min(1).optional(),
  projectId: uuidSchema.optional(),
  description: z4.string().optional(),
  batchSize: z4.coerce.number().int().positive().optional(),
  scriptContent: z4.string().optional(),
  status: campaignStatusEnum.optional(),
  outcomes: z4.array(campaignOutcomeSchema).optional(),
  operatorIds: z4.array(uuidSchema).optional()
});

// src/contact.ts
import { z as z5 } from "zod";
var createContactSchema = z5.object({
  firstName: z5.string().optional(),
  lastName: z5.string().optional(),
  email: optionalEmailSchema,
  phone: phoneSchema,
  tags: z5.array(z5.string()).optional(),
  projectId: uuidSchema.optional(),
  // Company
  companyId: uuidSchema.optional(),
  // Personal
  personalEmail: optionalEmailSchema,
  personalPhone: phoneSchema,
  position: z5.string().optional(),
  department: z5.string().optional(),
  linkedinUrl: z5.string().url({ message: "Neplatn\xE1 URL" }).optional().or(z5.literal("")),
  websiteUrl: z5.string().url({ message: "Neplatn\xE1 URL" }).optional().or(z5.literal("")),
  // Classification
  contactType: contactTypeEnum.optional(),
  contactScope: contactScopeEnum.optional(),
  temperature: temperatureEnum.optional(),
  // Source tracking
  source: z5.string().optional(),
  sourceDetail: z5.string().optional(),
  sourceUrl: z5.string().optional(),
  importBatchId: z5.string().optional(),
  // Consent / GDPR
  marketingEmailConsent: z5.boolean().optional(),
  marketingPhoneConsent: z5.boolean().optional(),
  marketingSmsConsent: z5.boolean().optional(),
  transactionalConsent: z5.boolean().optional(),
  consentGivenAt: z5.string().datetime().optional().or(z5.literal("")),
  consentSource: z5.string().optional(),
  consentIpAddress: z5.string().optional(),
  consentNote: z5.string().optional(),
  doNotContact: z5.boolean().optional(),
  doNotContactReason: z5.string().optional(),
  // Scoring
  score: z5.coerce.number().int().optional(),
  // Notes
  notes: z5.string().optional()
});
var updateContactSchema = createContactSchema.partial();
var bulkTagsSchema = z5.object({
  contactIds: z5.array(uuidSchema).min(1, { message: "Vyberte alespo\u0148 jeden kontakt" }),
  tags: z5.array(z5.string()).min(1, { message: "Zadejte alespo\u0148 jeden tag" })
});

// src/project.ts
import { z as z6 } from "zod";
var createProjectSchema = z6.object({
  name: z6.string({ required_error: "N\xE1zev projektu je povinn\xFD" }).min(1, { message: "N\xE1zev projektu je povinn\xFD" }),
  slug: z6.string().optional(),
  description: z6.string().optional(),
  color: z6.string().optional(),
  defaultPipelineId: uuidSchema.optional()
});

// src/valuo.ts
import { z as z7 } from "zod";
var propertyKindEnum = z7.enum(["sale", "lease"]);
var propertyTypeEnum = z7.enum(["flat", "house", "land"]);
var ratingEnum = z7.enum([
  "bad",
  "nothing_much",
  "good",
  "very_good",
  "new",
  "excellent"
]);
var ownershipEnum = z7.enum([
  "private",
  "cooperative",
  "council",
  "other"
]);
var houseTypeEnum = z7.enum([
  "family_house",
  "villa",
  "cottage",
  "hut",
  "other"
]);
var landTypeEnum = z7.enum([
  "building",
  "garden",
  "field",
  "meadow",
  "forest",
  "other"
]);
var constructionEnum = z7.enum([
  "brick",
  "panel",
  "wood",
  "stone",
  "montage",
  "mixed",
  "other"
]);
var visitorMetadataSchema = z7.object({
  screenWidth: z7.number().optional(),
  screenHeight: z7.number().optional(),
  viewportWidth: z7.number().optional(),
  viewportHeight: z7.number().optional(),
  deviceType: z7.string().optional(),
  platform: z7.string().optional(),
  isTouchDevice: z7.boolean().optional(),
  cookiesEnabled: z7.boolean().optional(),
  doNotTrack: z7.boolean().optional(),
  online: z7.boolean().optional(),
  userAgent: z7.string().optional(),
  language: z7.string().optional(),
  timezone: z7.string().optional(),
  languages: z7.array(z7.string()).optional(),
  timezoneOffset: z7.number().optional(),
  colorDepth: z7.number().optional(),
  pixelRatio: z7.number().optional(),
  connectionType: z7.string().optional(),
  connectionDownlink: z7.number().optional(),
  deviceMemory: z7.number().optional(),
  hardwareConcurrency: z7.number().optional(),
  utmSource: z7.string().optional(),
  utmMedium: z7.string().optional(),
  utmCampaign: z7.string().optional(),
  utmTerm: z7.string().optional(),
  utmContent: z7.string().optional(),
  pageUrl: z7.string().optional(),
  pageTitle: z7.string().optional(),
  referrerUrl: z7.string().optional(),
  timeOnPage: z7.number().optional(),
  formInteractions: z7.number().optional(),
  sessionId: z7.string().optional(),
  fingerprint: z7.string().optional()
});
var submitValuoSchema = z7.object({
  // Contact info (required)
  firstName: z7.string({ required_error: "Jmeno je povinne" }).min(1),
  lastName: z7.string({ required_error: "Prijmeni je povinne" }).min(1),
  email: emailSchema,
  phone: z7.string().optional(),
  // Property (required)
  kind: propertyKindEnum,
  propertyType: propertyTypeEnum,
  address: z7.string({ required_error: "Adresa je povinna" }).min(1),
  lat: z7.number({ required_error: "GPS souradnice jsou povinne" }),
  lng: z7.number({ required_error: "GPS souradnice jsou povinne" }),
  // Areas
  floorArea: z7.number().nonnegative().optional(),
  lotArea: z7.number().nonnegative().optional(),
  builtUpArea: z7.number().nonnegative().optional(),
  // Type & condition
  rating: ratingEnum.optional(),
  localType: z7.string().optional(),
  ownership: ownershipEnum.optional(),
  houseType: houseTypeEnum.optional(),
  landType: landTypeEnum.optional(),
  construction: constructionEnum.optional(),
  // Floor & elevator
  floor: z7.number().int().optional(),
  totalFloors: z7.number().int().positive().optional(),
  elevator: z7.boolean().optional(),
  // Energy & equipment
  energyPerformance: z7.string().optional(),
  equipment: z7.boolean().optional(),
  easyAccess: z7.boolean().optional(),
  // Additional areas
  loggiaArea: z7.number().nonnegative().optional(),
  atticArea: z7.number().nonnegative().optional(),
  cellarArea: z7.number().nonnegative().optional(),
  terraceArea: z7.number().nonnegative().optional(),
  balconyArea: z7.number().nonnegative().optional(),
  gardenArea: z7.number().nonnegative().optional(),
  otherArea: z7.number().nonnegative().optional(),
  // Room counts
  rooms: z7.number().int().nonnegative().optional(),
  bathrooms: z7.number().int().nonnegative().optional(),
  bedrooms: z7.number().int().nonnegative().optional(),
  kitchens: z7.number().int().nonnegative().optional(),
  // Parking
  garages: z7.number().int().nonnegative().optional(),
  parkingSpaces: z7.number().int().nonnegative().optional(),
  // Structured address
  street: z7.string().optional(),
  streetNumber: z7.string().optional(),
  city: z7.string().optional(),
  district: z7.string().optional(),
  region: z7.string().optional(),
  postalCode: z7.string().optional(),
  // Custom data
  data: z7.record(z7.any()).optional(),
  // Anti-spam honeypot — must be empty
  website: z7.string().max(0, { message: "Invalid submission detected" }).optional()
});
export {
  assignStrategyEnum,
  bulkTagsSchema,
  campaignOutcomeSchema,
  campaignStatusEnum,
  constructionEnum,
  contactScopeEnum,
  contactTypeEnum,
  createCampaignSchema,
  createContactSchema,
  createLeadSchema,
  createProjectSchema,
  emailSchema,
  houseTypeEnum,
  landTypeEnum,
  leadStatusEnum,
  leadTypeEnum,
  loginSchema,
  optionalEmailSchema,
  ownershipEnum,
  phoneSchema,
  propertyKindEnum,
  propertyTypeEnum,
  ratingEnum,
  registerSchema,
  submitValuoSchema,
  temperatureEnum,
  updateCampaignSchema,
  updateContactSchema,
  updateLeadSchema,
  uuidSchema,
  visitorMetadataSchema
};
//# sourceMappingURL=index.mjs.map